import React, { useState, useEffect, useCallback } from 'react';
import api from '../../../services/api';
import {
  Server, Play, Square, RefreshCw, Edit, Trash2, Plus, CheckCircle, XCircle, Clock,
  Activity, AlertCircle, X, Zap, Shield, Wifi, WifiOff, Settings, Eye, EyeOff,
  ArrowUpRight, ArrowDownRight, ToggleLeft, ToggleRight, ChevronDown, ChevronUp, Globe, Hash, Copy
} from 'lucide-react';
import { getApiBaseUrl } from '../../../config/apiBase';

const API_BASE_URL = getApiBaseUrl();

const ServerConnections = () => {
  const [connectors, setConnectors] = useState([]);
  const [statuses, setStatuses] = useState({});
  const [loading, setLoading] = useState(true);
  const [selectedConnector, setSelectedConnector] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [autoRefresh, setAutoRefresh] = useState(false);
  const [statusError, setStatusError] = useState(null);
  const [editingConnector, setEditingConnector] = useState(null);
  const [toast, setToast] = useState({ show: false, type: '', message: '' });
  const [expandedCard, setExpandedCard] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [actionLoading, setActionLoading] = useState({});
  const [showCloneModal, setShowCloneModal] = useState(false);
  const [cloneSource, setCloneSource] = useState(null);
  const [cloneName, setCloneName] = useState('');
  const [cloneLoading, setCloneLoading] = useState(false);
  const [deletedConnectors, setDeletedConnectors] = useState([]);
  const [showDeleted, setShowDeleted] = useState(false);
  const [restoreLoading, setRestoreLoading] = useState({});
  const [socketStatuses, setSocketStatuses] = useState({});
  const [formData, setFormData] = useState({
    connector_name: '',
    operator_name: '',
    host: '',
    port: 2775,
    system_id: '',
    password: '',
    system_type: '',
    bind_type: 'TRX',
    enquire_link_interval: 30,
    reconnect_interval: 5,
    max_reconnect_attempts: 10,
    tps_limit: 100,
    tps_mode: 'global',
    encoding_mode: 'AUTO',
    is_active: false,
    priority: 0,
    supports_long_sms: true,
    supports_unicode: true,
    supports_flash: true,
    supports_international: false,
    requires_dlt_validation: true,
    connection_timeout: 30,
    response_timeout: 10,
    window_size: 10,
    num_sockets: 1,
  });

  const showToast = useCallback((type, message) => {
    setToast({ show: true, type, message });
    setTimeout(() => setToast({ show: false, type: '', message: '' }), 4000);
  }, []);

  useEffect(() => {
    // Test backend connectivity first
    const testBackend = async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await api.get('/admin/users');
        console.log('Backend is reachable, users response:', response.data?.length || 0, 'users found');
      } catch (e) {
        console.error('Backend not reachable:', e);
      }
    };
    
    testBackend();
    fetchConnectors();
    fetchStatuses();
    fetchSocketStatuses();
  }, []);

  useEffect(() => {
    let interval;
    if (autoRefresh) {
      interval = setInterval(() => { fetchStatuses(); fetchSocketStatuses(); }, 10000);
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [autoRefresh]);

  const fetchConnectors = async () => {
    try {
      console.log('Fetching connectors...');
      const token = localStorage.getItem('token');
      console.log('Token exists:', !!token);
      console.log('API endpoint:', `${API_BASE_URL}/admin/smpp/connectors`);
      
      const response = await api.get('/admin/smpp/connectors');
      
      console.log('Connectors response:', response);
      console.log('Connectors data:', response.data);
      
      setConnectors(response.data || []);
      setLoading(false);
    } catch (error) {
      console.error('Failed to fetch connectors:', error);
      console.error('Error response:', error.response?.status, error.response?.data);
      setLoading(false);
    }
  };

  const fetchStatuses = async () => {
    try {
      setStatusError(null);
      const token = localStorage.getItem('token');
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      
      console.log('Current user:', user);
      console.log('User role:', user.role);
      console.log('Token exists:', !!token);
      
      // Use the correct admin endpoint
      console.log('Trying admin endpoint:', `${API_BASE_URL}/admin/smpp/statuses`);
      const response = await api.get('/admin/smpp/statuses');
      console.log('Admin endpoint success!');
      
      console.log('Statuses API response:', response);
      console.log('Response data:', response.data);
      
      // Handle null or undefined response data
      if (!response.data) {
        console.warn('Statuses API returned invalid data:', response.data);
        setStatuses({});
        return;
      }
      
      // The server status endpoint might return data in a different format
      // Check if it's an array of statuses or a single status object
      let statusArray = [];
      if (Array.isArray(response.data)) {
        statusArray = response.data;
      } else if (response.data.connectors && Array.isArray(response.data.connectors)) {
        statusArray = response.data.connectors;
      } else if (response.data.statuses && Array.isArray(response.data.statuses)) {
        statusArray = response.data.statuses;
      } else {
        // If it's a single status object, wrap it in an array
        statusArray = [response.data];
      }
      
      const statusMap = {};
      statusArray.forEach(status => {
        if (status && status.connector_id) {
          statusMap[status.connector_id] = status;
        }
      });
      setStatuses(statusMap);
    } catch (error) {
      console.error('Failed to fetch statuses:', error);
      console.error('Error response:', error.response);
      
      // Show specific error messages based on status code
      if (error.response?.status === 401) {
        window.location.reload(); return;
      } else if (error.response?.status === 403) {
        setStatusError('You do not have permission to access SMPP statuses. Master Admin access required.');
      } else if (error.response?.status === 404) {
        setStatusError('SMPP status endpoint not found. Please check server configuration.');
      } else {
        setStatusError('Failed to fetch connection statuses');
      }
      // Keep last known status as per requirements
    }
  };

  const fetchSocketStatuses = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await api.get('/admin/smpp/socket-statuses');
      if (response.data && typeof response.data === 'object') {
        setSocketStatuses(response.data);
      }
    } catch (error) {
      console.error('Failed to fetch socket statuses:', error);
    }
  };

  const fetchDeletedConnectors = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await api.get('/admin/smpp/connectors-deleted');
      setDeletedConnectors(response.data || []);
    } catch (error) {
      console.error('Failed to fetch deleted connectors:', error);
    }
  };

  const handleRestore = async (connectorId) => {
    if (!window.confirm('Restore this connector? It will be restored as inactive.')) return;
    try {
      setRestoreLoading(prev => ({ ...prev, [connectorId]: true }));
      const token = localStorage.getItem('token');
      await api.post(`/admin/smpp/connectors/${connectorId}/restore`);
      showToast('success', 'Connector restored successfully');
      fetchConnectors();
      fetchDeletedConnectors();
    } catch (error) {
      showToast('error', 'Failed to restore: ' + (error.response?.data?.error || error.message));
    } finally {
      setRestoreLoading(prev => ({ ...prev, [connectorId]: false }));
    }
  };

  const handleManualRefresh = () => {
    fetchConnectors();
    fetchStatuses();
    fetchSocketStatuses();
    if (showDeleted) fetchDeletedConnectors();
  };

  const handleStart = async (connectorId) => {
    try {
      setActionLoading(prev => ({ ...prev, [connectorId]: 'start' }));
      const token = localStorage.getItem('token');
      await api.post(`/admin/smpp/connectors/${connectorId}/start`);
      showToast('success', 'Connector started successfully');
      fetchConnectors();
      fetchStatuses();
    } catch (error) {
      showToast('error', 'Failed to start: ' + (error.response?.data?.error || error.message));
    } finally {
      setActionLoading(prev => ({ ...prev, [connectorId]: null }));
    }
  };

  const handleStop = async (connectorId) => {
    if (!window.confirm('Are you sure you want to stop this connector?')) return;
    
    try {
      setActionLoading(prev => ({ ...prev, [connectorId]: 'stop' }));
      const token = localStorage.getItem('token');
      await api.post(`/admin/smpp/connectors/${connectorId}/stop`);
      showToast('success', 'Connector stopped successfully');
      fetchConnectors();
      fetchStatuses();
    } catch (error) {
      showToast('error', 'Failed to stop: ' + (error.response?.data?.error || error.message));
    } finally {
      setActionLoading(prev => ({ ...prev, [connectorId]: null }));
    }
  };

  const handleRestart = async (connectorId) => {
    try {
      setActionLoading(prev => ({ ...prev, [connectorId]: 'restart' }));
      const token = localStorage.getItem('token');
      await api.post(`/admin/smpp/connectors/${connectorId}/start`);
      showToast('success', 'Connector restarted successfully');
      fetchConnectors();
      fetchStatuses();
    } catch (error) {
      showToast('error', 'Failed to restart: ' + (error.response?.data?.error || error.message));
    } finally {
      setActionLoading(prev => ({ ...prev, [connectorId]: null }));
    }
  };

  const handleCreate = () => {
    setEditingConnector(null);
    setFormData({
      connector_name: '',
      operator_name: '',
      host: '',
      port: 2775,
      system_id: '',
      password: '',
      system_type: '',
      bind_type: 'TRX',
      enquire_link_interval: 30,
      reconnect_interval: 5,
      max_reconnect_attempts: 10,
      tps_limit: 100,
      tps_mode: 'global',
      encoding_mode: 'AUTO',
      is_active: false,
      priority: 0,
      supports_long_sms: true,
      supports_unicode: true,
      supports_flash: true,
      supports_international: false,
      connection_timeout: 30,
      response_timeout: 10,
      window_size: 10,
      num_sockets: 1,
    });
    setShowModal(true);
  };

  const handleEdit = (connector) => {
    setEditingConnector(connector);
    setFormData({
      ...connector,
      password: '', // Don't populate password in edit mode
    });
    setShowModal(true);
  };

  const handleDelete = async (connectorId) => {
    if (!window.confirm('Are you sure you want to delete this connector?')) return;
    
    try {
      const token = localStorage.getItem('token');
      await api.delete(`/admin/smpp/connectors/${connectorId}`);
      showToast('success', 'Connector deleted successfully');
      fetchConnectors();
      if (showDeleted) fetchDeletedConnectors();
    } catch (error) {
      showToast('error', 'Failed to delete: ' + (error.response?.data?.error || error.message));
    }
  };

  const openCloneModal = (connector) => {
    setCloneSource(connector);
    setCloneName(connector.connector_name + '_clone');
    setShowCloneModal(true);
  };

  const handleClone = async () => {
    if (!cloneName.trim() || !cloneSource) return;
    
    try {
      setCloneLoading(true);
      const token = localStorage.getItem('token');
      await api.post(`/admin/smpp/connectors/${cloneSource.id}/clone`, 
        { connector_name: cloneName.trim() }
      );
      showToast('success', `Connector cloned as "${cloneName.trim()}"`);
      setShowCloneModal(false);
      setCloneSource(null);
      setCloneName('');
      fetchConnectors();
      fetchStatuses();
    } catch (error) {
      showToast('error', 'Failed to clone: ' + (error.response?.data?.error || error.message));
    } finally {
      setCloneLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const token = localStorage.getItem('token');
      const submitData = { ...formData };
      
      // Don't send password if it's empty in edit mode
      if (editingConnector && !submitData.password) {
        delete submitData.password;
      }
      
      if (editingConnector) {
        await api.put(`/admin/smpp/connectors/${editingConnector.id}`, submitData);
        showToast('success', 'Connector updated successfully');
      } else {
        await api.post('/admin/smpp/connectors', submitData);
        showToast('success', 'Connector created successfully');
      }
      
      setShowModal(false);
      fetchConnectors();
    } catch (error) {
      showToast('error', 'Failed to save: ' + (error.response?.data?.error || error.message));
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    let finalValue = value;
    
    // Handle numeric inputs
    if (type === 'number') {
      finalValue = parseInt(value, 10) || 0;
    }
    
    // Handle checkboxes
    if (type === 'checkbox') {
      finalValue = checked;
    }
    
    setFormData(prev => ({
      ...prev,
      [name]: finalValue
    }));
  };

  const cardStyle = { border: '1px solid rgba(99, 102, 241, 0.08)', boxShadow: '0 1px 3px rgba(99, 102, 241, 0.06)' };
  const inputClass = "w-full h-9 px-3 text-sm rounded-lg bg-white text-gray-800 focus:outline-none focus:ring-2 focus:ring-indigo-500 transition-colors";
  const inputBorder = { border: '1px solid rgba(99, 102, 241, 0.15)' };
  const labelClass = "block text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1.5";

  const getConnectionInfo = (connectorId) => {
    const status = statuses[connectorId];
    if (!status || !status.connection_state) return { state: 'DISCONNECTED', color: '#6b7280', bg: 'rgba(107, 114, 128, 0.08)', border: 'rgba(107, 114, 128, 0.15)', icon: WifiOff, label: 'Disconnected' };
    
    const s = status.connection_state.toUpperCase();
    if (s === 'BOUND' || s === 'CONNECTED') return { state: s, color: '#059669', bg: 'rgba(16, 185, 129, 0.08)', border: 'rgba(16, 185, 129, 0.2)', icon: Wifi, label: 'Connected' };
    if (s === 'BINDING' || s === 'RECONNECTING' || s === 'CONNECTING') return { state: s, color: '#d97706', bg: 'rgba(245, 158, 11, 0.08)', border: 'rgba(245, 158, 11, 0.2)', icon: RefreshCw, label: 'Reconnecting' };
    if (s === 'FAILED' || s === 'ERROR' || s === 'REJECTED') return { state: s, color: '#dc2626', bg: 'rgba(239, 68, 68, 0.08)', border: 'rgba(239, 68, 68, 0.2)', icon: XCircle, label: 'Failed' };
    return { state: s, color: '#6b7280', bg: 'rgba(107, 114, 128, 0.08)', border: 'rgba(107, 114, 128, 0.15)', icon: WifiOff, label: 'Disconnected' };
  };

  // KPI calculations
  const totalConnectors = connectors.length;
  const connectedCount = connectors.filter(c => { const s = statuses[c.id]?.connection_state?.toUpperCase(); return s === 'BOUND' || s === 'CONNECTED'; }).length;
  const failedCount = connectors.filter(c => { const s = statuses[c.id]?.connection_state?.toUpperCase(); return s === 'FAILED' || s === 'ERROR' || s === 'REJECTED'; }).length;
  const disconnectedCount = totalConnectors - connectedCount - failedCount;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <RefreshCw className="w-8 h-8 animate-spin mx-auto mb-3" style={{ color: '#6366f1' }} />
          <p className="text-gray-500">Loading SMPP connectors...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-5">
      {/* Toast Notification */}
      {toast.show && (
        <div className="fixed top-4 right-4 z-[60] animate-slide-in">
          <div className="flex items-center gap-3 px-4 py-3 rounded-xl shadow-lg text-white text-sm font-medium" style={{
            background: toast.type === 'success' ? 'linear-gradient(135deg, #059669, #10b981)' : 'linear-gradient(135deg, #dc2626, #ef4444)',
            minWidth: '280px'
          }}>
            {toast.type === 'success' ? <CheckCircle className="w-4 h-4 flex-shrink-0" /> : <AlertCircle className="w-4 h-4 flex-shrink-0" />}
            <span className="flex-1">{toast.message}</span>
            <button onClick={() => setToast({ show: false, type: '', message: '' })} className="ml-2 hover:opacity-80"><X className="w-3.5 h-3.5" /></button>
          </div>
        </div>
      )}

      {/* Page Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #4f46e5, #06b6d4)' }}>
              <Server className="w-5 h-5 text-white" />
            </div>
            Outbound SMPP Connections
          </h1>
          <p className="text-sm text-gray-500 mt-1">Manage, monitor and control SMPP server connections</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {/* Auto-refresh toggle */}
          <button
            onClick={() => setAutoRefresh(!autoRefresh)}
            className="flex items-center gap-2 px-3 py-2 rounded-xl text-xs font-medium transition-all"
            style={{
              background: autoRefresh ? 'rgba(16, 185, 129, 0.08)' : 'rgba(107, 114, 128, 0.06)',
              border: `1px solid ${autoRefresh ? 'rgba(16, 185, 129, 0.2)' : 'rgba(107, 114, 128, 0.12)'}`,
              color: autoRefresh ? '#059669' : '#6b7280'
            }}
          >
            {autoRefresh ? <ToggleRight className="w-4 h-4" /> : <ToggleLeft className="w-4 h-4" />}
            Auto-refresh {autoRefresh ? 'ON' : 'OFF'}
          </button>
          <button
            onClick={handleManualRefresh}
            className="flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-medium transition-all hover:shadow-sm"
            style={{ background: 'rgba(99, 102, 241, 0.06)', border: '1px solid rgba(99, 102, 241, 0.12)', color: '#4f46e5' }}
          >
            <RefreshCw className="w-3.5 h-3.5" />
            Refresh
          </button>
          <button
            onClick={handleCreate}
            className="flex items-center gap-1.5 px-4 py-2 rounded-xl text-xs font-semibold text-white shadow-sm transition-all hover:shadow-md"
            style={{ background: 'linear-gradient(135deg, #4f46e5, #06b6d4)' }}
          >
            <Plus className="w-3.5 h-3.5" />
            Add Connector
          </button>
        </div>
      </div>

      {/* KPI Summary Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <div className="rounded-xl p-3 flex items-center gap-3 transition-all" style={{ background: 'rgba(99, 102, 241, 0.06)', border: '1px solid rgba(99, 102, 241, 0.12)' }}>
          <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #4f46e5, #6366f1)' }}>
            <Server className="w-4 h-4 text-white" />
          </div>
          <div>
            <div className="text-xl font-bold text-gray-900">{totalConnectors}</div>
            <div className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider">Total</div>
          </div>
        </div>
        <div className="rounded-xl p-3 flex items-center gap-3 transition-all" style={{ background: 'rgba(16, 185, 129, 0.06)', border: '1px solid rgba(16, 185, 129, 0.12)' }}>
          <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #059669, #34d399)' }}>
            <Wifi className="w-4 h-4 text-white" />
          </div>
          <div>
            <div className="text-xl font-bold text-gray-900">{connectedCount}</div>
            <div className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider">Connected</div>
          </div>
        </div>
        <div className="rounded-xl p-3 flex items-center gap-3 transition-all" style={{ background: 'rgba(107, 114, 128, 0.06)', border: '1px solid rgba(107, 114, 128, 0.12)' }}>
          <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #6b7280, #9ca3af)' }}>
            <WifiOff className="w-4 h-4 text-white" />
          </div>
          <div>
            <div className="text-xl font-bold text-gray-900">{disconnectedCount}</div>
            <div className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider">Disconnected</div>
          </div>
        </div>
        <div className="rounded-xl p-3 flex items-center gap-3 transition-all" style={{ background: 'rgba(239, 68, 68, 0.06)', border: '1px solid rgba(239, 68, 68, 0.12)' }}>
          <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #dc2626, #ef4444)' }}>
            <XCircle className="w-4 h-4 text-white" />
          </div>
          <div>
            <div className="text-xl font-bold text-gray-900">{failedCount}</div>
            <div className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider">Failed</div>
          </div>
        </div>
      </div>

      {/* Status Error Banner */}
      {statusError && (
        <div className="rounded-xl p-3 flex items-center gap-3" style={{ background: 'rgba(245, 158, 11, 0.06)', border: '1px solid rgba(245, 158, 11, 0.15)' }}>
          <AlertCircle className="w-4 h-4 flex-shrink-0" style={{ color: '#d97706' }} />
          <span className="text-xs font-medium text-amber-800 flex-1">{statusError}</span>
          <span className="text-[10px] text-amber-600">Status unavailable</span>
        </div>
      )}

      {/* Connector Cards */}
      {connectors.length === 0 ? (
        <div className="bg-white rounded-xl p-12 text-center" style={cardStyle}>
          <div className="w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-4" style={{ background: 'rgba(99, 102, 241, 0.06)' }}>
            <Server className="w-8 h-8 text-gray-300" />
          </div>
          <h3 className="text-lg font-bold text-gray-900 mb-2">No SMPP Connectors</h3>
          <p className="text-sm text-gray-500 mb-6">Get started by adding your first outbound SMPP connector.</p>
          <button onClick={handleCreate}
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-semibold text-white shadow-md hover:shadow-lg transition-all"
            style={{ background: 'linear-gradient(135deg, #4f46e5, #06b6d4)' }}>
            <Plus className="w-4 h-4" /> Add Your First Connector
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {connectors.map(connector => {
            const status = statuses[connector.id];
            const connInfo = getConnectionInfo(connector.id);
            const isExpanded = expandedCard === connector.id;
            const isActioning = actionLoading[connector.id];
            const ConnIcon = connInfo.icon;

            return (
              <div key={connector.id} className="bg-white rounded-xl overflow-hidden transition-all duration-200 hover:shadow-md" style={cardStyle}>
                {/* Card Header — Status Bar */}
                <div className="px-4 py-2 flex items-center justify-between" style={{ background: connInfo.bg, borderBottom: `1px solid ${connInfo.border}` }}>
                  <div className="flex items-center gap-2">
                    <ConnIcon className="w-3.5 h-3.5" style={{ color: connInfo.color }} />
                    <span className="text-xs font-bold uppercase tracking-wider" style={{ color: connInfo.color }}>{connInfo.label}</span>
                    {autoRefresh && connInfo.label === 'Connected' && (
                      <span className="relative flex h-2 w-2">
                        <span className="animate-ping absolute inline-flex h-full w-full rounded-full opacity-75" style={{ background: connInfo.color }}></span>
                        <span className="relative inline-flex rounded-full h-2 w-2" style={{ background: connInfo.color }}></span>
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-1.5">
                    <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] font-bold uppercase"
                      style={{
                        background: connector.requires_dlt_validation ? 'rgba(99, 102, 241, 0.1)' : 'rgba(245, 158, 11, 0.1)',
                        color: connector.requires_dlt_validation ? '#4f46e5' : '#d97706',
                      }}>
                      <Shield className="w-2.5 h-2.5" />
                      {connector.requires_dlt_validation ? 'DLT' : 'INTL'}
                    </span>
                    {connector.is_active && (
                      <span className="px-1.5 py-0.5 rounded text-[9px] font-bold uppercase" style={{ background: 'rgba(16, 185, 129, 0.1)', color: '#059669' }}>Active</span>
                    )}
                    <span className="px-1.5 py-0.5 rounded text-[9px] font-bold uppercase" style={{ background: 'rgba(99, 102, 241, 0.08)', color: '#4f46e5' }}>{connector.bind_type}</span>
                    <span className="px-1.5 py-0.5 rounded text-[9px] font-bold uppercase" style={{ background: 'rgba(6, 182, 212, 0.08)', color: '#0891b2' }}>P{connector.priority}</span>
                  </div>
                </div>

                {/* Card Body */}
                <div className="p-4">
                  {/* Title Row */}
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-3 min-w-0">
                      <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: 'linear-gradient(135deg, rgba(79, 70, 229, 0.1), rgba(6, 182, 212, 0.1))' }}>
                        <Server className="w-5 h-5" style={{ color: '#4f46e5' }} />
                      </div>
                      <div className="min-w-0">
                        <h3 className="text-sm font-bold text-gray-900 truncate">{connector.connector_name}</h3>
                        <p className="text-xs text-gray-500 truncate">{connector.operator_name}</p>
                      </div>
                    </div>
                  </div>

                  {/* Info Grid */}
                  <div className="grid grid-cols-3 gap-2 mb-3">
                    <div className="rounded-lg p-2" style={{ background: 'rgba(99, 102, 241, 0.03)', border: '1px solid rgba(99, 102, 241, 0.06)' }}>
                      <div className="text-[9px] font-bold text-gray-400 uppercase tracking-wider">Host:Port</div>
                      <div className="text-xs font-mono font-semibold text-gray-800 truncate" title={`${connector.host}:${connector.port}`}>{connector.host}:{connector.port}</div>
                    </div>
                    <div className="rounded-lg p-2" style={{ background: 'rgba(99, 102, 241, 0.03)', border: '1px solid rgba(99, 102, 241, 0.06)' }}>
                      <div className="text-[9px] font-bold text-gray-400 uppercase tracking-wider">System ID</div>
                      <div className="text-xs font-mono font-semibold text-gray-800 truncate">{connector.system_id}</div>
                    </div>
                    <div className="rounded-lg p-2" style={{ background: 'rgba(99, 102, 241, 0.03)', border: '1px solid rgba(99, 102, 241, 0.06)' }}>
                      <div className="text-[9px] font-bold text-gray-400 uppercase tracking-wider">Encoding</div>
                      <div className="text-xs font-semibold text-gray-800">{connector.encoding_mode || 'AUTO'}</div>
                    </div>
                  </div>

                  {/* TPS & Activity Row */}
                  <div className="flex items-center gap-3 mb-3">
                    <div className="flex-1 rounded-lg p-2 flex items-center gap-2" style={{ background: 'rgba(6, 182, 212, 0.04)', border: '1px solid rgba(6, 182, 212, 0.1)' }}>
                      <Zap className="w-3.5 h-3.5" style={{ color: '#0891b2' }} />
                      <div>
                        <div className="text-[9px] font-bold text-gray-400 uppercase">TPS</div>
                        <div className="text-xs font-bold text-gray-800">
                          <span style={{ color: '#0891b2' }}>{status?.current_tps || 0}</span>
                          <span className="text-gray-400"> / {connector.tps_limit}</span>
                        </div>
                      </div>
                    </div>
                    <div className="flex-1 rounded-lg p-2 flex items-center gap-2" style={{ background: 'rgba(139, 92, 246, 0.04)', border: '1px solid rgba(139, 92, 246, 0.1)' }}>
                      <Activity className="w-3.5 h-3.5" style={{ color: '#8b5cf6' }} />
                      <div>
                        <div className="text-[9px] font-bold text-gray-400 uppercase">Last Activity</div>
                        <div className="text-xs font-medium text-gray-700 truncate">
                          {status?.updated_at ? new Date(status.updated_at).toLocaleTimeString() : 'Never'}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Socket Grid Visualization */}
                  {(connector.num_sockets > 0) && (
                    <div className="mb-3 rounded-lg p-2.5" style={{ background: 'rgba(99, 102, 241, 0.03)', border: '1px solid rgba(99, 102, 241, 0.08)' }}>
                      <div className="flex items-center justify-between mb-2">
                        <div className="text-[9px] font-bold text-gray-400 uppercase tracking-wider">
                          Sockets ({connector.num_sockets}) &middot; Window Size: {connector.window_size || 10}
                        </div>
                        <div className="flex items-center gap-3 text-[9px] text-gray-400">
                          <span className="flex items-center gap-1"><span className="inline-block w-2 h-2 rounded-full" style={{ background: '#10b981' }}></span> Connected</span>
                          <span className="flex items-center gap-1"><span className="inline-block w-2 h-2 rounded-full" style={{ background: '#ef4444' }}></span> Disconnected</span>
                        </div>
                      </div>
                      <div className="flex flex-wrap gap-1.5">
                        {Array.from({ length: connector.num_sockets || 1 }, (_, idx) => {
                          const sockets = socketStatuses[connector.id] || [];
                          const sock = sockets.find(s => s.socket_index === idx);
                          const sockState = sock?.connection_state?.toUpperCase() || 'DISCONNECTED';
                          const isConnected = sockState === 'BOUND' || sockState === 'CONNECTED';
                          const isConnecting = sockState === 'CONNECTING';
                          return (
                            <div
                              key={idx}
                              title={`Socket ${idx + 1}: ${sockState}${sock?.remote_address ? ' (' + sock.remote_address + ')' : ''}${sock?.messages_sent ? ' | Sent: ' + sock.messages_sent : ''}`}
                              className="flex items-center justify-center rounded-md text-[9px] font-bold transition-all cursor-default"
                              style={{
                                width: connector.num_sockets <= 10 ? '32px' : '24px',
                                height: connector.num_sockets <= 10 ? '32px' : '24px',
                                background: isConnected
                                  ? 'linear-gradient(135deg, #059669, #10b981)'
                                  : isConnecting
                                    ? 'linear-gradient(135deg, #d97706, #f59e0b)'
                                    : 'linear-gradient(135deg, #dc2626, #ef4444)',
                                color: '#fff',
                                boxShadow: isConnected
                                  ? '0 2px 6px rgba(16, 185, 129, 0.4)'
                                  : isConnecting
                                    ? '0 2px 6px rgba(245, 158, 11, 0.4)'
                                    : '0 2px 6px rgba(239, 68, 68, 0.3)',
                              }}
                            >
                              {idx + 1}
                            </div>
                          );
                        })}
                      </div>
                      {/* Socket summary line */}
                      {(() => {
                        const sockets = socketStatuses[connector.id] || [];
                        const connectedSockets = sockets.filter(s => {
                          const st = s.connection_state?.toUpperCase();
                          return st === 'BOUND' || st === 'CONNECTED';
                        }).length;
                        return (
                          <div className="mt-1.5 text-[10px] font-semibold" style={{ color: connectedSockets === (connector.num_sockets || 1) ? '#059669' : connectedSockets > 0 ? '#d97706' : '#dc2626' }}>
                            {connectedSockets} / {connector.num_sockets || 1} sockets connected
                          </div>
                        );
                      })()}
                    </div>
                  )}

                  {/* Expanded Details */}
                  {isExpanded && (
                    <div className="mb-3 space-y-2">
                      {/* Live Stats from Status API */}
                      {status && (
                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                          <div className="text-center p-2 rounded-lg" style={{ background: 'rgba(16, 185, 129, 0.05)', border: '1px solid rgba(16, 185, 129, 0.1)' }}>
                            <div className="text-base font-bold" style={{ color: '#059669' }}>{status.messages_sent_today || 0}</div>
                            <div className="text-[9px] font-bold text-gray-400 uppercase">Sent Today</div>
                          </div>
                          <div className="text-center p-2 rounded-lg" style={{ background: 'rgba(239, 68, 68, 0.05)', border: '1px solid rgba(239, 68, 68, 0.1)' }}>
                            <div className="text-base font-bold" style={{ color: '#dc2626' }}>{status.messages_failed_today || 0}</div>
                            <div className="text-[9px] font-bold text-gray-400 uppercase">Failed Today</div>
                          </div>
                          <div className="text-center p-2 rounded-lg" style={{ background: 'rgba(99, 102, 241, 0.05)', border: '1px solid rgba(99, 102, 241, 0.1)' }}>
                            <div className="text-base font-bold text-gray-800">{status.reconnect_attempts || 0}</div>
                            <div className="text-[9px] font-bold text-gray-400 uppercase">Reconnects</div>
                          </div>
                          <div className="text-center p-2 rounded-lg" style={{ background: 'rgba(6, 182, 212, 0.05)', border: '1px solid rgba(6, 182, 212, 0.1)' }}>
                            <div className="text-base font-bold text-gray-800">
                              {status.uptime_seconds > 0 ? (status.uptime_seconds > 3600 ? Math.floor(status.uptime_seconds / 3600) + 'h' : Math.floor(status.uptime_seconds / 60) + 'm') : '—'}
                            </div>
                            <div className="text-[9px] font-bold text-gray-400 uppercase">Uptime</div>
                          </div>
                        </div>
                      )}

                      {/* Timestamps */}
                      {status && (
                        <div className="grid grid-cols-2 gap-2 text-xs p-2 rounded-lg" style={{ background: 'rgba(99, 102, 241, 0.02)', border: '1px solid rgba(99, 102, 241, 0.06)' }}>
                          <div><span className="text-gray-400">Last Bind:</span> <span className="font-semibold text-gray-700">{status.last_bind_time ? new Date(status.last_bind_time).toLocaleString() : '—'}</span></div>
                          <div><span className="text-gray-400">Last Enquire:</span> <span className="font-semibold text-gray-700">{status.last_enquire_link ? new Date(status.last_enquire_link).toLocaleString() : '—'}</span></div>
                          <div><span className="text-gray-400">Updated:</span> <span className="font-semibold text-gray-700">{status.updated_at ? new Date(status.updated_at).toLocaleString() : '—'}</span></div>
                          {status.last_error && (
                            <div className="col-span-2"><span className="text-red-400">Last Error:</span> <span className="font-semibold text-red-600">{status.last_error}</span></div>
                          )}
                        </div>
                      )}

                      {/* Config Details */}
                      <div className="p-2 rounded-lg" style={{ background: 'rgba(99, 102, 241, 0.02)', border: '1px solid rgba(99, 102, 241, 0.06)' }}>
                        <div className="grid grid-cols-2 gap-2 text-xs">
                          <div><span className="text-gray-400">Enquire Link:</span> <span className="font-semibold text-gray-700">{connector.enquire_link_interval}s</span></div>
                          <div><span className="text-gray-400">Reconnect:</span> <span className="font-semibold text-gray-700">{connector.reconnect_interval}s</span></div>
                          <div><span className="text-gray-400">Max Retries:</span> <span className="font-semibold text-gray-700">{connector.max_reconnect_attempts}</span></div>
                          <div><span className="text-gray-400">Conn Timeout:</span> <span className="font-semibold text-gray-700">{connector.connection_timeout}s</span></div>
                          <div><span className="text-gray-400">Resp Timeout:</span> <span className="font-semibold text-gray-700">{connector.response_timeout}s</span></div>
                          <div><span className="text-gray-400">System Type:</span> <span className="font-semibold text-gray-700">{connector.system_type || '—'}</span></div>
                        </div>
                      </div>

                      {/* Feature Flags */}
                      <div className="flex flex-wrap gap-2">
                        {/* DLT Validation - prominent badge */}
                        <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-[10px] font-bold"
                          style={{
                            background: connector.requires_dlt_validation ? 'rgba(99, 102, 241, 0.08)' : 'rgba(245, 158, 11, 0.08)',
                            color: connector.requires_dlt_validation ? '#4f46e5' : '#d97706',
                            border: `1px solid ${connector.requires_dlt_validation ? 'rgba(99, 102, 241, 0.15)' : 'rgba(245, 158, 11, 0.15)'}`,
                          }}>
                          <Shield className="w-3 h-3" />
                          PE-TM / DLT: {connector.requires_dlt_validation ? 'Required' : 'Not Required'}
                        </span>
                        {[
                          { label: 'Long SMS', val: connector.supports_long_sms },
                          { label: 'Unicode', val: connector.supports_unicode },
                          { label: 'Flash', val: connector.supports_flash },
                          { label: 'International', val: connector.supports_international },
                        ].map(f => (
                          <span key={f.label} className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-semibold"
                            style={{ background: f.val ? 'rgba(16, 185, 129, 0.08)' : 'rgba(239, 68, 68, 0.06)', color: f.val ? '#059669' : '#dc2626' }}>
                            {f.val ? <CheckCircle className="w-2.5 h-2.5" /> : <XCircle className="w-2.5 h-2.5" />}
                            {f.label}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Action Buttons */}
                  <div className="flex items-center gap-1.5 pt-2 flex-wrap" style={{ borderTop: '1px solid rgba(99, 102, 241, 0.06)' }}>
                    {/* Start */}
                    <button onClick={() => handleStart(connector.id)} disabled={!!isActioning || connector.is_active}
                      className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-[11px] font-semibold transition-all hover:shadow-sm disabled:opacity-40"
                      style={{ background: 'rgba(16, 185, 129, 0.06)', color: '#059669', border: '1px solid rgba(16, 185, 129, 0.15)' }}>
                      {isActioning === 'start' ? <RefreshCw className="w-3 h-3 animate-spin" /> : <Play className="w-3 h-3" />} Start
                    </button>
                    {/* Stop */}
                    <button onClick={() => handleStop(connector.id)} disabled={!!isActioning || !connector.is_active}
                      className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-[11px] font-semibold transition-all hover:shadow-sm disabled:opacity-40"
                      style={{ background: 'rgba(239, 68, 68, 0.06)', color: '#dc2626', border: '1px solid rgba(239, 68, 68, 0.15)' }}>
                      {isActioning === 'stop' ? <RefreshCw className="w-3 h-3 animate-spin" /> : <Square className="w-3 h-3" />} Stop
                    </button>
                    {/* Restart */}
                    <button onClick={() => handleRestart(connector.id)} disabled={!!isActioning}
                      className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-[11px] font-semibold transition-all hover:shadow-sm disabled:opacity-50"
                      style={{ background: 'rgba(6, 182, 212, 0.06)', color: '#0891b2', border: '1px solid rgba(6, 182, 212, 0.15)' }}>
                      {isActioning === 'restart' ? <RefreshCw className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />} Restart
                    </button>

                    <div className="flex-1" />

                    {/* Expand */}
                    <button onClick={() => setExpandedCard(isExpanded ? null : connector.id)}
                      className="flex items-center gap-1 px-2 py-1.5 rounded-lg text-[11px] font-medium transition-all"
                      style={{ color: '#6b7280' }}>
                      {isExpanded ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
                      {isExpanded ? 'Less' : 'More'}
                    </button>
                    {/* Clone */}
                    <button onClick={() => openCloneModal(connector)}
                      className="flex items-center gap-1 px-2 py-1.5 rounded-lg text-[11px] font-medium transition-all hover:shadow-sm"
                      style={{ color: '#8b5cf6', background: 'rgba(139, 92, 246, 0.06)', border: '1px solid rgba(139, 92, 246, 0.1)' }}
                      title="Clone this connector">
                      <Copy className="w-3 h-3" /> Clone
                    </button>
                    {/* Edit */}
                    <button onClick={() => handleEdit(connector)}
                      className="flex items-center gap-1 px-2 py-1.5 rounded-lg text-[11px] font-medium transition-all hover:shadow-sm"
                      style={{ color: '#4f46e5', background: 'rgba(99, 102, 241, 0.06)', border: '1px solid rgba(99, 102, 241, 0.1)' }}>
                      <Edit className="w-3 h-3" /> Edit
                    </button>
                    {/* Delete */}
                    <button onClick={() => handleDelete(connector.id)}
                      className="flex items-center gap-1 px-2 py-1.5 rounded-lg text-[11px] font-medium transition-all hover:shadow-sm"
                      style={{ color: '#dc2626', background: 'rgba(239, 68, 68, 0.04)', border: '1px solid rgba(239, 68, 68, 0.1)' }}>
                      <Trash2 className="w-3 h-3" />
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Deleted Connectors Section */}
      <div className="mt-6">
        <button
          onClick={() => { setShowDeleted(!showDeleted); if (!showDeleted) fetchDeletedConnectors(); }}
          className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-xs font-bold uppercase tracking-wider transition-all hover:shadow-sm"
          style={{
            background: showDeleted ? 'rgba(239, 68, 68, 0.06)' : 'rgba(107, 114, 128, 0.06)',
            color: showDeleted ? '#dc2626' : '#6b7280',
            border: `1px solid ${showDeleted ? 'rgba(239, 68, 68, 0.15)' : 'rgba(107, 114, 128, 0.12)'}`,
          }}>
          <Trash2 className="w-3.5 h-3.5" />
          Deleted Connectors {deletedConnectors.length > 0 && showDeleted ? `(${deletedConnectors.length})` : ''}
          {showDeleted ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
        </button>

        {showDeleted && (
          <div className="mt-3">
            {deletedConnectors.length === 0 ? (
              <div className="text-center py-8 text-gray-400 text-sm">No deleted connectors</div>
            ) : (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                {deletedConnectors.map(connector => (
                  <div key={connector.id} className="bg-white rounded-xl overflow-hidden transition-all duration-200 opacity-70 hover:opacity-100"
                    style={{ border: '1px solid rgba(239, 68, 68, 0.15)', boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }}>
                    <div className="px-4 py-2 flex items-center justify-between" style={{ background: 'rgba(239, 68, 68, 0.04)', borderBottom: '1px solid rgba(239, 68, 68, 0.1)' }}>
                      <div className="flex items-center gap-2">
                        <Trash2 className="w-3.5 h-3.5 text-red-400" />
                        <span className="text-xs font-bold uppercase tracking-wider text-red-400">Deleted</span>
                      </div>
                      <div className="flex items-center gap-1.5">
                        <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] font-bold uppercase"
                          style={{
                            background: connector.requires_dlt_validation ? 'rgba(99, 102, 241, 0.1)' : 'rgba(245, 158, 11, 0.1)',
                            color: connector.requires_dlt_validation ? '#4f46e5' : '#d97706',
                          }}>
                          <Shield className="w-2.5 h-2.5" />
                          {connector.requires_dlt_validation ? 'DLT' : 'INTL'}
                        </span>
                        <span className="px-1.5 py-0.5 rounded text-[9px] font-bold uppercase" style={{ background: 'rgba(99, 102, 241, 0.08)', color: '#4f46e5' }}>{connector.bind_type}</span>
                      </div>
                    </div>
                    <div className="p-4 flex items-center justify-between">
                      <div className="flex items-center gap-3 min-w-0">
                        <div className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: 'rgba(239, 68, 68, 0.06)' }}>
                          <Server className="w-4 h-4 text-red-300" />
                        </div>
                        <div className="min-w-0">
                          <h3 className="text-sm font-bold text-gray-500 truncate">{connector.connector_name}</h3>
                          <p className="text-[10px] text-gray-400 truncate">{connector.host}:{connector.port} · {connector.system_id}</p>
                        </div>
                      </div>
                      <button
                        onClick={() => handleRestore(connector.id)}
                        disabled={restoreLoading[connector.id]}
                        className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[11px] font-bold transition-all hover:shadow-sm disabled:opacity-50"
                        style={{ background: 'rgba(16, 185, 129, 0.08)', color: '#059669', border: '1px solid rgba(16, 185, 129, 0.2)' }}>
                        {restoreLoading[connector.id] ? <RefreshCw className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
                        Restore
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Add/Edit Connector Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-hidden flex flex-col" style={{ border: '1px solid rgba(99, 102, 241, 0.1)' }}>
            {/* Modal Header */}
            <div className="flex items-center justify-between px-6 py-4" style={{ background: 'linear-gradient(135deg, rgba(79, 70, 229, 0.04), rgba(6, 182, 212, 0.04))', borderBottom: '1px solid rgba(99, 102, 241, 0.08)' }}>
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #4f46e5, #06b6d4)' }}>
                  {editingConnector ? <Edit className="w-4 h-4 text-white" /> : <Plus className="w-4 h-4 text-white" />}
                </div>
                <div>
                  <h3 className="text-base font-bold text-gray-900">{editingConnector ? 'Edit Connector' : 'New SMPP Connector'}</h3>
                  <p className="text-xs text-gray-500">{editingConnector ? 'Update connection parameters' : 'Configure a new outbound SMPP connection'}</p>
                </div>
              </div>
              <button onClick={() => setShowModal(false)} className="w-8 h-8 rounded-lg flex items-center justify-center hover:bg-gray-100 transition-colors">
                <X className="w-4 h-4 text-gray-400" />
              </button>
            </div>

            {/* Modal Body */}
            <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto px-6 py-5 space-y-5">
              {/* Connection Details */}
              <div>
                <h4 className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-3 flex items-center gap-2">
                  <Server className="w-3.5 h-3.5" style={{ color: '#6366f1' }} /> Connection Details
                </h4>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label className={labelClass}>Connector Name *</label>
                    <input type="text" name="connector_name" value={formData.connector_name} onChange={handleInputChange} className={inputClass} style={inputBorder} required placeholder="e.g. way2trmpg" />
                  </div>
                  <div>
                    <label className={labelClass}>Operator Name *</label>
                    <input type="text" name="operator_name" value={formData.operator_name} onChange={handleInputChange} className={inputClass} style={inputBorder} required placeholder="e.g. Way2Connect" />
                  </div>
                  <div>
                    <label className={labelClass}>Host *</label>
                    <input type="text" name="host" value={formData.host} onChange={handleInputChange} className={inputClass} style={inputBorder} required placeholder="e.g. 45.114.140.155" />
                  </div>
                  <div>
                    <label className={labelClass}>Port *</label>
                    <input type="number" name="port" value={formData.port} onChange={handleInputChange} className={inputClass} style={inputBorder} required />
                  </div>
                </div>
              </div>

              {/* Authentication */}
              <div>
                <h4 className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-3 flex items-center gap-2">
                  <Shield className="w-3.5 h-3.5" style={{ color: '#6366f1' }} /> Authentication
                </h4>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label className={labelClass}>System ID *</label>
                    <input type="text" name="system_id" value={formData.system_id} onChange={handleInputChange} className={inputClass} style={inputBorder} required />
                  </div>
                  <div>
                    <label className={labelClass}>Password {editingConnector ? '' : '*'}</label>
                    <div className="relative">
                      <input type={showPassword ? 'text' : 'password'} name="password" value={formData.password} onChange={handleInputChange}
                        className={inputClass + ' pr-9'} style={inputBorder}
                        required={!editingConnector} placeholder={editingConnector ? 'Leave blank to keep current' : ''} />
                      <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                        {showPassword ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
                      </button>
                    </div>
                  </div>
                  <div>
                    <label className={labelClass}>System Type</label>
                    <input type="text" name="system_type" value={formData.system_type} onChange={handleInputChange} className={inputClass} style={inputBorder} placeholder="Optional" />
                  </div>
                  <div>
                    <label className={labelClass}>Bind Type *</label>
                    <select name="bind_type" value={formData.bind_type} onChange={handleInputChange} className={inputClass} style={inputBorder}>
                      <option value="TX">TX (Transmitter)</option>
                      <option value="RX">RX (Receiver)</option>
                      <option value="TRX">TRX (Transceiver)</option>
                    </select>
                  </div>
                </div>
              </div>

              {/* Performance */}
              <div>
                <h4 className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-3 flex items-center gap-2">
                  <Zap className="w-3.5 h-3.5" style={{ color: '#6366f1' }} /> Performance & Timing
                </h4>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                  <div>
                    <label className={labelClass}>TPS Limit</label>
                    <input type="number" name="tps_limit" value={formData.tps_limit} onChange={handleInputChange} className={inputClass} style={inputBorder} />
                  </div>
                  <div>
                    <label className={labelClass}>TPS Mode</label>
                    <select name="tps_mode" value={formData.tps_mode || 'global'} onChange={handleInputChange} className={inputClass} style={inputBorder}>
                      <option value="global">Global (Shared TPS)</option>
                      <option value="socket_base">Socket Base (Per-Socket TPS)</option>
                    </select>
                  </div>
                  <div>
                    <label className={labelClass}>Priority</label>
                    <input type="number" name="priority" value={formData.priority} onChange={handleInputChange} className={inputClass} style={inputBorder} />
                  </div>
                  <div>
                    <label className={labelClass}>Encoding Mode</label>
                    <select name="encoding_mode" value={formData.encoding_mode} onChange={handleInputChange} className={inputClass} style={inputBorder}>
                      <option value="AUTO">Auto</option>
                      <option value="GSM7">GSM 7-bit</option>
                      <option value="UCS2">UCS-2 (Unicode)</option>
                    </select>
                  </div>
                  <div>
                    <label className={labelClass}>Enquire Link (sec)</label>
                    <input type="number" name="enquire_link_interval" value={formData.enquire_link_interval} onChange={handleInputChange} className={inputClass} style={inputBorder} />
                  </div>
                  <div>
                    <label className={labelClass}>Reconnect (sec)</label>
                    <input type="number" name="reconnect_interval" value={formData.reconnect_interval} onChange={handleInputChange} className={inputClass} style={inputBorder} />
                  </div>
                  <div>
                    <label className={labelClass}>Max Retries</label>
                    <input type="number" name="max_reconnect_attempts" value={formData.max_reconnect_attempts} onChange={handleInputChange} className={inputClass} style={inputBorder} />
                  </div>
                  <div>
                    <label className={labelClass}>Conn Timeout (sec)</label>
                    <input type="number" name="connection_timeout" value={formData.connection_timeout} onChange={handleInputChange} className={inputClass} style={inputBorder} />
                  </div>
                  <div>
                    <label className={labelClass}>Resp Timeout (sec)</label>
                    <input type="number" name="response_timeout" value={formData.response_timeout} onChange={handleInputChange} className={inputClass} style={inputBorder} />
                  </div>
                  <div>
                    <label className={labelClass}>Window Size</label>
                    <input type="number" name="window_size" value={formData.window_size} onChange={handleInputChange} className={inputClass} style={inputBorder} min="1" max="100" />
                  </div>
                  <div>
                    <label className={labelClass}>Num Sockets</label>
                    <input type="number" name="num_sockets" value={formData.num_sockets} onChange={handleInputChange} className={inputClass} style={inputBorder} min="1" max="20" />
                  </div>
                </div>
              </div>

              {/* Feature Toggles */}
              <div>
                <h4 className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-3 flex items-center gap-2">
                  <Settings className="w-3.5 h-3.5" style={{ color: '#6366f1' }} /> Features & Capabilities
                </h4>
                {/* PE-TM / DLT Validation Toggle */}
                <label className="flex items-center gap-3 p-3 rounded-xl cursor-pointer transition-all mb-3"
                  style={{
                    background: formData.requires_dlt_validation ? 'rgba(99, 102, 241, 0.06)' : 'rgba(245, 158, 11, 0.06)',
                    border: `1px solid ${formData.requires_dlt_validation ? 'rgba(99, 102, 241, 0.15)' : 'rgba(245, 158, 11, 0.15)'}`,
                  }}>
                  <input type="checkbox" name="requires_dlt_validation" checked={formData.requires_dlt_validation} onChange={handleInputChange} className="sr-only" />
                  <div className="w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0" style={{
                    background: formData.requires_dlt_validation ? 'linear-gradient(135deg, #4f46e5, #06b6d4)' : 'rgba(245, 158, 11, 0.15)',
                  }}>
                    <Shield className="w-4 h-4" style={{ color: formData.requires_dlt_validation ? '#fff' : '#d97706' }} />
                  </div>
                  <div className="flex-1">
                    <div className="text-xs font-bold" style={{ color: formData.requires_dlt_validation ? '#4f46e5' : '#d97706' }}>
                      PE-TM Chain / DLT Validation
                    </div>
                    <div className="text-[10px] text-gray-500">
                      {formData.requires_dlt_validation
                        ? 'Enabled — DLT TLVs (PE_ID, Template_ID, Hash) will be sent in SMPP PDU'
                        : 'Disabled — No DLT TLVs sent (for international SMPP connectors)'}
                    </div>
                  </div>
                  <div className="text-[10px] font-bold px-2 py-0.5 rounded-full" style={{
                    background: formData.requires_dlt_validation ? 'rgba(16, 185, 129, 0.1)' : 'rgba(245, 158, 11, 0.1)',
                    color: formData.requires_dlt_validation ? '#059669' : '#d97706',
                  }}>
                    {formData.requires_dlt_validation ? 'ON' : 'OFF'}
                  </div>
                </label>

                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                  {[
                    { name: 'is_active', label: 'Active', icon: CheckCircle },
                    { name: 'supports_long_sms', label: 'Long SMS', icon: Globe },
                    { name: 'supports_unicode', label: 'Unicode', icon: Hash },
                    { name: 'supports_flash', label: 'Flash SMS', icon: Zap },
                    { name: 'supports_international', label: 'International', icon: Globe },
                  ].map(toggle => (
                    <label key={toggle.name} className="flex items-center gap-2 p-2.5 rounded-lg cursor-pointer transition-all"
                      style={{
                        background: formData[toggle.name] ? 'rgba(16, 185, 129, 0.06)' : 'rgba(107, 114, 128, 0.03)',
                        border: `1px solid ${formData[toggle.name] ? 'rgba(16, 185, 129, 0.15)' : 'rgba(107, 114, 128, 0.08)'}`,
                      }}>
                      <input type="checkbox" name={toggle.name} checked={formData[toggle.name]} onChange={handleInputChange} className="sr-only" />
                      <div className="w-5 h-5 rounded flex items-center justify-center flex-shrink-0" style={{
                        background: formData[toggle.name] ? 'rgba(16, 185, 129, 0.15)' : 'rgba(107, 114, 128, 0.08)',
                      }}>
                        {formData[toggle.name] ? <CheckCircle className="w-3 h-3" style={{ color: '#059669' }} /> : <XCircle className="w-3 h-3 text-gray-400" />}
                      </div>
                      <span className="text-xs font-medium" style={{ color: formData[toggle.name] ? '#059669' : '#6b7280' }}>{toggle.label}</span>
                    </label>
                  ))}
                </div>
              </div>
            </form>

            {/* Modal Footer */}
            <div className="flex items-center justify-end gap-2 px-6 py-3" style={{ borderTop: '1px solid rgba(99, 102, 241, 0.08)', background: 'rgba(99, 102, 241, 0.02)' }}>
              <button type="button" onClick={() => setShowModal(false)}
                className="px-4 py-2 rounded-xl text-sm font-medium text-gray-600 hover:bg-gray-100 transition-all"
                style={{ border: '1px solid rgba(99, 102, 241, 0.1)' }}>
                Cancel
              </button>
              <button type="submit" onClick={handleSubmit}
                className="px-5 py-2 rounded-xl text-sm font-semibold text-white shadow-sm transition-all hover:shadow-md"
                style={{ background: 'linear-gradient(135deg, #4f46e5, #06b6d4)' }}>
                {editingConnector ? 'Update Connector' : 'Create Connector'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Clone Connector Modal */}
      {showCloneModal && cloneSource && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden" style={{ border: '1px solid rgba(99, 102, 241, 0.1)' }}>
            {/* Header */}
            <div className="flex items-center justify-between px-6 py-4" style={{ background: 'linear-gradient(135deg, rgba(139, 92, 246, 0.04), rgba(6, 182, 212, 0.04))', borderBottom: '1px solid rgba(99, 102, 241, 0.08)' }}>
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #8b5cf6, #06b6d4)' }}>
                  <Copy className="w-4 h-4 text-white" />
                </div>
                <div>
                  <h3 className="text-base font-bold text-gray-900">Clone Connector</h3>
                  <p className="text-xs text-gray-500">Duplicate configuration to a new connector</p>
                </div>
              </div>
              <button onClick={() => { setShowCloneModal(false); setCloneSource(null); }} className="w-8 h-8 rounded-lg flex items-center justify-center hover:bg-gray-100 transition-colors">
                <X className="w-4 h-4 text-gray-400" />
              </button>
            </div>

            {/* Body */}
            <div className="px-6 py-5 space-y-4">
              {/* Source Info */}
              <div className="rounded-xl p-3 flex items-center gap-3" style={{ background: 'rgba(99, 102, 241, 0.03)', border: '1px solid rgba(99, 102, 241, 0.08)' }}>
                <div className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: 'linear-gradient(135deg, rgba(79, 70, 229, 0.1), rgba(6, 182, 212, 0.1))' }}>
                  <Server className="w-4 h-4" style={{ color: '#4f46e5' }} />
                </div>
                <div className="min-w-0">
                  <div className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">Cloning From</div>
                  <div className="text-sm font-bold text-gray-900 truncate">{cloneSource.connector_name}</div>
                  <div className="text-xs text-gray-500 truncate">{cloneSource.operator_name} &middot; {cloneSource.host}:{cloneSource.port}</div>
                </div>
              </div>

              {/* Clone Name Input */}
              <div>
                <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1.5">New Connector Name *</label>
                <input
                  type="text"
                  value={cloneName}
                  onChange={(e) => setCloneName(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleClone()}
                  className="w-full h-10 px-3 text-sm rounded-lg bg-white text-gray-800 focus:outline-none focus:ring-2 focus:ring-indigo-500 transition-colors"
                  style={{ border: '1px solid rgba(99, 102, 241, 0.15)' }}
                  placeholder="Enter a unique name for the clone"
                  autoFocus
                />
              </div>

              {/* What gets cloned */}
              <div className="rounded-lg p-3" style={{ background: 'rgba(139, 92, 246, 0.03)', border: '1px solid rgba(139, 92, 246, 0.08)' }}>
                <div className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-2">What will be cloned</div>
                <div className="grid grid-cols-2 gap-1.5">
                  {['Host & Port', 'System ID & Password', 'Bind Type', 'TPS & Priority', 'Encoding Mode', 'Timeouts & Intervals', 'Feature Flags', 'DLT Validation Setting'].map(item => (
                    <div key={item} className="flex items-center gap-1.5 text-xs text-gray-600">
                      <CheckCircle className="w-3 h-3 flex-shrink-0" style={{ color: '#8b5cf6' }} />
                      {item}
                    </div>
                  ))}
                  <div className="flex items-center gap-1.5 text-xs text-gray-500">
                    <XCircle className="w-3 h-3 flex-shrink-0 text-gray-400" />
                    <span className="text-gray-400">Active status (off)</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="flex items-center justify-end gap-2 px-6 py-3" style={{ borderTop: '1px solid rgba(99, 102, 241, 0.08)', background: 'rgba(99, 102, 241, 0.02)' }}>
              <button onClick={() => { setShowCloneModal(false); setCloneSource(null); }}
                className="px-4 py-2 rounded-xl text-sm font-medium text-gray-600 hover:bg-gray-100 transition-all"
                style={{ border: '1px solid rgba(99, 102, 241, 0.1)' }}>
                Cancel
              </button>
              <button onClick={handleClone} disabled={!cloneName.trim() || cloneLoading}
                className="px-5 py-2 rounded-xl text-sm font-semibold text-white shadow-sm transition-all hover:shadow-md disabled:opacity-50 flex items-center gap-2"
                style={{ background: 'linear-gradient(135deg, #8b5cf6, #06b6d4)' }}>
                {cloneLoading ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <Copy className="w-3.5 h-3.5" />}
                {cloneLoading ? 'Cloning...' : 'Clone Connector'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ServerConnections;
